import React, { useState } from 'react';
import { Star, Gift, Users, TrendingUp, Copy, Share2, ChevronRight, CheckCircle, Lock, ArrowLeft, ShoppingBag, Award } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useI18n } from '../i18n';
import { LOYALTY_PROGRAMS } from '../data/database';

const StarProgramPage = ({ onNavigate, onBack }) => {
  const { user, activateStarProgram } = useApp();
  const { t } = useI18n();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [copied, setCopied] = useState(false);
  
  // Get program config
  const starProgram = LOYALTY_PROGRAMS.star;
  
  // Get current and next level from database
  // I livelli Star partono da 1, 0 significa non ancora nel programma
  const currentLevel = starProgram.levels.find(l => l.level === (user.starLevel || 1)) || starProgram.levels[0];
  const currentLevelIndex = starProgram.levels.findIndex(l => l.level === currentLevel.level);
  const nextLevel = starProgram.levels[currentLevelIndex + 1];
  const referralsForNext = nextLevel ? nextLevel.minReferrals - (user.starReferrals || 0) : 0;
  const progressPercent = nextLevel 
    ? Math.max(0, (((user.starReferrals || 0) - currentLevel.minReferrals) / (nextLevel.minReferrals - currentLevel.minReferrals)) * 100)
    : 100;

  const referralCode = user.starReferralCode || 'ALEX2026';
  const referralLink = `skipres.com/r/${referralCode}`;

  const copyLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Icon mapping for dynamic rendering
  const iconMap = { Share2, Users, Gift, Star, TrendingUp, Award };

  // If user is not a Star member, show activation page
  if (!user.isStarMember) {
    return (
      <div className="min-h-screen bg-gray-50 pb-20">
        {/* Header */}
        <div className="bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-800 pt-12 pb-20 relative overflow-hidden">
          <button onClick={onBack} className="absolute top-4 left-4 w-10 h-10 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center z-10">
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <div className="absolute inset-0 opacity-20 pointer-events-none">
            <div className="absolute top-10 right-10 w-40 h-40 bg-white rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-60 h-60 bg-purple-300 rounded-full blur-3xl" />
          </div>
          <div className="relative text-center pt-6">
            <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Star className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">{starProgram.name}</h1>
            <p className="text-purple-200">{starProgram.description}</p>
          </div>
        </div>

        {/* Progress Card */}
        <div className="max-w-screen-2xl mx-auto px-6 -mt-10 relative z-10">
          <div className="bg-white rounded-2xl shadow-lg p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm text-gray-500">Livello attuale</p>
                <p className="text-xl font-bold text-gray-900">{currentLevel.name}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">Commissione</p>
                <p className="text-lg font-bold text-purple-600">{currentLevel.commission}%</p>
              </div>
            </div>
            <p className="text-sm text-gray-600 text-center">
              Attiva il programma Star per iniziare a guadagnare
            </p>
          </div>
        </div>

        {/* How it works - Dynamic from database */}
        <div className="max-w-screen-2xl mx-auto px-6 mt-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Come funziona</h2>
          <div className="bg-white rounded-xl p-4 space-y-4">
            {starProgram.howItWorks.map((step, index) => {
              const IconComponent = iconMap[step.icon] || Star;
              return (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <IconComponent className="w-4 h-4 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{step.title}</p>
                    <p className="text-sm text-gray-600">{step.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* All Levels - Dynamic with benefits */}
        <div className="max-w-screen-2xl mx-auto px-6 mt-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Tutti i livelli Star</h2>
          <div className="space-y-3">
            {starProgram.levels.map((level) => (
              <div 
                key={level.level}
                className={`bg-white rounded-xl p-4 relative ${level.level === (user.starLevel || 1) ? 'ring-2 ring-purple-500' : ''}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${level.level === (user.starLevel || 1) ? 'bg-purple-100' : 'bg-gray-100'}`}>
                      <Star className={`w-5 h-5 ${level.level === (user.starLevel || 1) ? 'text-purple-600' : 'text-gray-400'}`} />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{level.name}</p>
                      <p className="text-sm text-gray-500">{level.minReferrals}+ referral</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-purple-600">{level.commission}%</p>
                    <p className="text-xs text-gray-500">commissione</p>
                  </div>
                </div>
                {/* Benefits from database */}
                <div className="pl-13 space-y-1">
                  {level.benefits?.map((benefit, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-gray-600">
                      <CheckCircle className="w-3.5 h-3.5 text-purple-500 flex-shrink-0" />
                      <span>{benefit}</span>
                    </div>
                  ))}
                </div>
                {level.level === (user.starLevel || 1) && (
                  <div className="absolute -top-1 -right-1 bg-purple-500 text-white text-xs px-2 py-0.5 rounded-full">
                    Attuale
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="max-w-screen-2xl mx-auto px-6 mt-6">
          <button 
            onClick={activateStarProgram}
            className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-4 rounded-2xl font-semibold flex items-center justify-center gap-2 shadow-lg shadow-purple-500/30 hover:shadow-xl transition-all"
          >
            Attiva Programma Star
            <ChevronRight className="w-5 h-5" />
          </button>
          <p className="text-center text-sm text-gray-500 mt-3">
            Gratuito • Inizia subito a guadagnare
          </p>
        </div>
      </div>
    );
  }
  
  // User is Star member - show dashboard
  const EARNINGS_DATA = {
    total: user.starEarnings,
    thisMonth: 0,
    pending: 0,
    referrals: user.starReferrals || 0,
    conversions: 0,
    history: []
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-800 pt-12 pb-20 relative overflow-hidden">
        <button onClick={onBack} className="absolute top-4 left-4 w-10 h-10 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center z-10">
          <ArrowLeft className="w-5 h-5 text-white" />
        </button>
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <div className="absolute top-10 right-10 w-40 h-40 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-60 h-60 bg-purple-300 rounded-full blur-3xl" />
        </div>
        <div className="relative text-center pt-6">
          <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Star className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Star {currentLevel.name}</h1>
          <p className="text-purple-200">Commissione attuale: <span className="font-bold text-white">{currentLevel.commission}%</span></p>
        </div>
      </div>

      {/* Progress Card */}
      <div className="max-w-screen-2xl mx-auto px-6 -mt-10 relative z-10">
        <div className="bg-white rounded-2xl shadow-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-gray-500">Referral totali</p>
              <p className="text-2xl font-bold text-gray-900">{EARNINGS_DATA.referrals}</p>
            </div>
            {nextLevel && (
              <div className="text-right">
                <p className="text-sm text-gray-500">Prossimo livello</p>
                <p className="text-lg font-bold text-purple-500">{nextLevel.name}</p>
              </div>
            )}
          </div>
          
          {nextLevel ? (
            <>
              <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                <div 
                  className="bg-gradient-to-r from-purple-500 to-indigo-500 h-3 rounded-full transition-all duration-500"
                  style={{ width: `${Math.min(progressPercent, 100)}%` }}
                />
              </div>
              <p className="text-sm text-gray-600 text-center">
                Ancora <span className="font-bold text-purple-600">{referralsForNext} referral</span> per raggiungere Star {nextLevel.name} ({nextLevel.commission}% commissione)
              </p>
            </>
          ) : (
            <div className="text-center py-3 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl">
              <p className="text-purple-700 font-medium">Hai raggiunto il livello massimo!</p>
            </div>
          )}
        </div>
      </div>

      {/* Earnings Summary */}
      <div className="max-w-screen-2xl mx-auto px-6 mt-6">
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-sm text-gray-500">Guadagni totali</p>
            <p className="text-2xl font-bold text-gray-900">€{EARNINGS_DATA.total.toFixed(2)}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-sm text-gray-500">Questo mese</p>
            <p className="text-2xl font-bold text-gray-900">€{EARNINGS_DATA.thisMonth.toFixed(2)}</p>
          </div>
        </div>
      </div>

      {/* Referral Link */}
      <div className="max-w-screen-2xl mx-auto px-6 mt-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">Il tuo link di invito</h2>
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-2xl p-5">
          <div className="flex items-center gap-2 bg-white rounded-xl p-3">
            <input
              type="text"
              value={referralLink}
              readOnly
              className="flex-1 bg-transparent text-gray-700 outline-none text-sm"
            />
            <button 
              onClick={copyLink}
              className={`p-2 rounded-lg transition-all ${copied ? 'bg-emerald-500 text-white' : 'bg-purple-100 text-purple-600 hover:bg-purple-200'}`}
            >
              {copied ? <CheckCircle className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
            </button>
          </div>
          <button className="w-full mt-4 bg-purple-600 text-white py-3 rounded-xl font-medium flex items-center justify-center gap-2 hover:bg-purple-700 transition-colors">
            <Share2 className="w-5 h-5" />
            Condividi Link
          </button>
        </div>
      </div>

      {/* All Levels */}
      <div className="max-w-screen-2xl mx-auto px-6 mt-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">Tutti i livelli Star</h2>
        <div className="space-y-3">
          {starProgram.levels.map((level) => (
            <div 
              key={level.level}
              className={`bg-white rounded-xl p-4 flex items-center justify-between relative ${level.level === user.starLevel ? 'ring-2 ring-purple-500' : ''}`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${level.level === user.starLevel ? 'bg-purple-100' : 'bg-gray-100'}`}>
                  <Star className={`w-5 h-5 ${level.level === user.starLevel ? 'text-purple-600' : 'text-gray-400'}`} />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Star {level.name}</p>
                  <p className="text-sm text-gray-500">{level.minReferrals}+ referral</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-purple-600">{level.commission}%</p>
                <p className="text-xs text-gray-500">commissione</p>
              </div>
              {level.level === user.starLevel && (
                <div className="absolute -top-1 -right-1 bg-purple-500 text-white text-xs px-2 py-0.5 rounded-full">
                  Attuale
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* How it works */}
      <div className="max-w-screen-2xl mx-auto px-6 mt-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">Come funziona</h2>
        <div className="bg-white rounded-xl p-4 space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Share2 className="w-4 h-4 text-purple-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Condividi il tuo link</p>
              <p className="text-sm text-gray-600">Invita amici a usare Skipres con il tuo codice personale</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <ShoppingBag className="w-4 h-4 text-emerald-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">I tuoi amici prenotano o acquistano</p>
              <p className="text-sm text-gray-600">Servizi, eventi, corsi o prodotti - ogni transazione conta</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Gift className="w-4 h-4 text-amber-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Guadagni commissioni</p>
              <p className="text-sm text-gray-600">Ricevi una percentuale su ogni transazione dei tuoi invitati</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StarProgramPage;
